import { useState, useEffect, useMemo } from 'react';
import { Link, useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { ScrollArea } from '../components/ui/scroll-area';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  getCalls,
  getCallDetail,
  getBalance,
  getCustomerAgents,
  patchCall,
} from '../lib/api';
import { formatDateTime, formatListActivityTime, buildTelHref, cn } from '../lib/utils';
import { toast } from 'sonner';
import { getUsedVoiceMinutesFromBalance, getRemainingVoiceMinutesFromBalance } from '../lib/usageMetrics';

/** Align with AgentsPages / dashboard: list payloads often omit minutes_used but include duration_seconds. */
function minutesFromCall(call) {
  if (call == null) return 0;
  if (call.minutes_used != null && `${call.minutes_used}`.trim() !== '') {
    const n = Number(call.minutes_used);
    if (Number.isFinite(n)) return n;
  }
  if (call.duration_minutes != null && `${call.duration_minutes}`.trim() !== '') {
    const n = Number(call.duration_minutes);
    if (Number.isFinite(n)) return n;
  }
  if (call.duration_seconds != null && `${call.duration_seconds}`.trim() !== '') {
    const n = Number(call.duration_seconds);
    if (Number.isFinite(n)) return n / 60;
  }
  return 0;
}

function sumMinutesFromCallsList(calls) {
  if (!Array.isArray(calls)) return 0;
  return calls.reduce((sum, c) => sum + minutesFromCall(c), 0);
}
import {
  PhoneCall,
  Clock,
  FileText,
  ArrowLeft,
  Play,
  ChevronRight,
  ChevronLeft,
  Timer,
  Search,
  TrendingUp,
  BarChart3,
  MessageSquare,
  User,
  ExternalLink,
  MoreVertical,
  UserCheck,
  RotateCcw,
  List,
  LayoutList,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { IntentSemanticsIcon, SentimentSemanticsIcon } from '../components/call/CallSemanticsIcon';

const getNestedValue = (obj, path) =>
  path.split('.').reduce((acc, key) => (acc && acc[key] !== undefined ? acc[key] : null), obj);

const getFirstCallValue = (call, paths = []) => {
  for (const path of paths) {
    const value = getNestedValue(call, path);
    if (value !== undefined && value !== null && `${value}`.trim() !== '') {
      return value;
    }
  }
  return null;
};

const getCallIntent = (call) =>
  getFirstCallValue(call, [
    'intent',
    'call.intent',
    'summary_details.intent',
    'analysis.intent',
    'ai.intent',
  ]);

const getCallCustomerSentiment = (call) =>
  getFirstCallValue(call, [
    'customerSentiment',
    'customer_sentiment',
    'call.customerSentiment',
    'call.customer_sentiment',
    'summary_details.customer_sentiment',
    'analysis.customer_sentiment',
    'customerDetails.customerSentiment',
    'customer_details.customer_sentiment',
  ]);

const getCallListPhone = (call) =>
  getFirstCallValue(call, [
    'customer_details.contact_phone',
    'customerDetails.contactPhone',
    'customer.phone',
    'customer_phone',
    'from',
    'caller_number',
    'callerNumber',
  ]) || 'Unknown number';

const getCallListName = (call) =>
  getFirstCallValue(call, [
    'customer_details.caller_name',
    'customerDetails.callerName',
    'customer.name',
    'customer_name',
    'customerName',
  ]) || '';

export function CallLogsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [calls, setCalls] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [stats, setStats] = useState({ total_minutes: 0, remaining_minutes: 0 });
  const [agents, setAgents] = useState([]);
  const [agentFilter, setAgentFilter] = useState(searchParams.get('agentId') || 'all');
  const [markingCallId, setMarkingCallId] = useState(null);
  const [listView, setListView] = useState('simple');
  const limit = 20;

  useEffect(() => {
    loadAgents();
  }, []);

  useEffect(() => {
    loadCalls();
    loadStats();
  }, [page, agentFilter]);

  useEffect(() => {
    setPage(0);
  }, [agentFilter]);

  const loadAgents = async () => {
    try {
      const response = await getCustomerAgents({ limit: 200 });
      const list = Array.isArray(response?.data?.agents) ? response.data.agents : [];
      setAgents(list);
    } catch {
      // Agent list endpoint may not be available in all environments
      setAgents([]);
    }
  };

  const loadCalls = async () => {
    try {
      setLoading(true);
      const params = { limit, skip: page * limit };
      if (agentFilter !== 'all') {
        params.agent_id = agentFilter;
        params.agentId = agentFilter;
      }
      const response = await getCalls(params);
      setCalls(response.data.calls);
      setTotal(response.data.total);
    } catch (error) {
      toast.error('Failed to load calls');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    const resolveMinutesUsedFromCalls = (callsRes) => {
      const calls = callsRes?.data?.calls;
      const summed = sumMinutesFromCallsList(calls);
      if (summed > 0) return summed;
      const top = Number(callsRes?.data?.total_minutes ?? callsRes?.data?.totalMinutes);
      return Number.isFinite(top) && top > 0 ? top : summed;
    };

    try {
      const params = { limit: 2000, skip: 0 };
      if (agentFilter !== 'all') {
        params.agent_id = agentFilter;
        params.agentId = agentFilter;
      }
      const [callsRes, balanceRes] = await Promise.all([getCalls(params), getBalance()]);
      const fromBalance = getUsedVoiceMinutesFromBalance(balanceRes);
      const fromCalls = resolveMinutesUsedFromCalls(callsRes);
      const rawUsed = fromBalance != null ? fromBalance : fromCalls;
      const minutesUsed = Math.round(rawUsed * 100) / 100;

      setStats({
        total_minutes: minutesUsed,
        remaining_minutes: getRemainingVoiceMinutesFromBalance(balanceRes),
      });
    } catch (error) {
      console.error('Failed to load stats');
    }
  };

  const totalPages = Math.ceil(total / limit);

  // Filter calls by search term
  const filteredCalls = calls.filter((call) => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    const phone = `${getCallListPhone(call)}`.toLowerCase();
    const name = `${getCallListName(call)}`.toLowerCase();
    return (
      call.call_id.toLowerCase().includes(search) ||
      (call.summary && call.summary.toLowerCase().includes(search)) ||
      (call.transcript && call.transcript.toLowerCase().includes(search)) ||
      phone.includes(search) ||
      (name && name.includes(search))
    );
  });

  const displayCalls = useMemo(() => {
    return [...filteredCalls].sort((a, b) => {
      const ac = Boolean(a.contacted);
      const bc = Boolean(b.contacted);
      if (ac !== bc) return ac ? 1 : -1;
      return 0;
    });
  }, [filteredCalls]);

  const handleSetCallContacted = async (call, contacted, e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    setMarkingCallId(call.call_id);
    try {
      await patchCall(call.call_id, { contacted });
      toast.success(contacted ? 'Marked as contacted' : 'Marked as not contacted');
      await loadCalls();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update call');
    } finally {
      setMarkingCallId(null);
    }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="call-logs-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-jakarta font-bold text-slate-900">Call History</h1>
          <p className="text-slate-500 mt-1">
            Voice calls with transcripts, summaries, sentiment, and next-step signals—use Messages for SMS, WhatsApp,
            and Facebook
          </p>
        </div>
        <div className="w-full sm:w-72">
          <Select
            value={agentFilter}
            onValueChange={(value) => {
              setAgentFilter(value);
              const next = new URLSearchParams(searchParams);
              if (value === 'all') {
                next.delete('agentId');
              } else {
                next.set('agentId', value);
              }
              setSearchParams(next);
            }}
          >
            <SelectTrigger>
              <SelectValue placeholder="Filter by agent" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Agents</SelectItem>
              {agents.map((agent) => (
                <SelectItem key={agent.agent_id} value={agent.agent_id}>
                  {agent.name || agent.agent_id}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="border-0 shadow-sm bg-gradient-to-br from-blue-50 to-blue-100/50">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-500 flex items-center justify-center">
                <PhoneCall className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-xs text-blue-600 font-medium">Total Calls</p>
                <p className="text-2xl font-bold text-blue-900">{total}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm bg-gradient-to-br from-green-50 to-green-100/50">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-500 flex items-center justify-center">
                <Timer className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-xs text-green-600 font-medium">Minutes Used</p>
                <p className="text-2xl font-bold text-green-900">{stats.total_minutes}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm bg-gradient-to-br from-amber-50 to-amber-100/50">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-500 flex items-center justify-center">
                <Clock className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-xs text-amber-600 font-medium">Remaining Minutes</p>
                <p className="text-2xl font-bold text-amber-900">{stats.remaining_minutes}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search & list view */}
      <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
        <div className="relative flex-1 max-w-xl">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Search calls by ID, summary, or transcript..."
            className="pl-10"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            data-testid="call-search-input"
          />
        </div>
        <div
          className="flex items-center gap-1 rounded-lg border border-slate-200 bg-slate-50 p-0.5 shrink-0"
          role="group"
          aria-label="List layout"
        >
          <Button
            type="button"
            variant={listView === 'simple' ? 'secondary' : 'ghost'}
            size="sm"
            className="h-8 px-3 gap-1.5"
            onClick={() => setListView('simple')}
            data-testid="call-list-view-simple"
          >
            <List className="w-4 h-4" />
            Simple
          </Button>
          <Button
            type="button"
            variant={listView === 'detailed' ? 'secondary' : 'ghost'}
            size="sm"
            className="h-8 px-3 gap-1.5"
            onClick={() => setListView('detailed')}
            data-testid="call-list-view-detailed"
          >
            <LayoutList className="w-4 h-4" />
            Detailed
          </Button>
        </div>
      </div>

      {/* Calls List */}
      <Card className="border-0 shadow-md">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 space-y-4">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="flex items-center gap-4">
                  <Skeleton className="w-10 h-10 rounded-full" />
                  <div className="flex-1">
                    <Skeleton className="h-4 w-32 mb-2" />
                    <Skeleton className="h-3 w-48" />
                  </div>
                  <Skeleton className="h-4 w-20" />
                </div>
              ))}
            </div>
          ) : filteredCalls.length === 0 ? (
            <div className="text-center py-16">
              <PhoneCall className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-slate-900 mb-2">
                {searchTerm ? 'No matching calls' : 'No calls yet'}
              </h3>
              <p className="text-slate-500 max-w-sm mx-auto">
                {searchTerm 
                  ? 'Try adjusting your search terms'
                  : 'Your call logs will appear here once your AI voice agents start handling calls.'}
              </p>
            </div>
          ) : (
            <>
              <div className="divide-y divide-slate-100">
                {displayCalls.map((call) => {
                  const callIntent = getCallIntent(call);
                  const customerSentiment = getCallCustomerSentiment(call);
                  const listName = getCallListName(call);
                  const listPhone = getCallListPhone(call);
                  const telHref = buildTelHref(listPhone);
                  return (
                    <div
                      key={call.call_id}
                      className={cn(
                        'flex items-stretch min-h-[72px]',
                        call.contacted && 'opacity-70 grayscale-[0.35] bg-slate-50/90',
                      )}
                      data-testid={`call-row-${call.call_id}`}
                    >
                      {listView === 'simple' ? (
                        <div
                          role="link"
                          tabIndex={0}
                          className={cn(
                            'flex-1 min-w-0 p-4 hover:bg-slate-50 transition-colors flex cursor-pointer outline-none focus-visible:ring-2 focus-visible:ring-teal-500/30',
                            call.contacted && 'hover:bg-slate-100/80',
                          )}
                          onClick={() => navigate(`/calls/${call.call_id}`)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              navigate(`/calls/${call.call_id}`);
                            }
                          }}
                        >
                          <div className="flex items-start justify-between gap-3 w-full">
                            <div className="flex items-start gap-3 flex-1 min-w-0">
                              <div
                                className="shrink-0 pt-0.5"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                }}
                              >
                                {customerSentiment ? (
                                  <SentimentSemanticsIcon rawValue={customerSentiment} size={18} />
                                ) : (
                                  <span
                                    className={cn(
                                      'inline-flex h-7 w-7 items-center justify-center rounded-full ring-1 ring-inset bg-slate-100 text-slate-500 ring-slate-200',
                                      call.contacted && 'opacity-80',
                                    )}
                                    aria-hidden
                                  >
                                    <PhoneCall className="w-3.5 h-3.5" />
                                  </span>
                                )}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-baseline gap-2 flex-wrap min-w-0">
                                  {telHref ? (
                                    <a
                                      href={telHref}
                                      onClick={(e) => e.stopPropagation()}
                                      onKeyDown={(e) => e.stopPropagation()}
                                      className={cn(
                                        'text-base font-semibold tracking-tight text-blue-600 hover:underline shrink-0',
                                        call.contacted && 'text-blue-500/90',
                                      )}
                                    >
                                      {listPhone}
                                    </a>
                                  ) : (
                                    <span
                                      className={cn(
                                        'text-base font-semibold tracking-tight',
                                        call.contacted ? 'text-slate-600' : 'text-slate-900',
                                      )}
                                    >
                                      {listPhone}
                                    </span>
                                  )}
                                  {listName ? (
                                    <span className="text-sm text-slate-400 truncate">{listName}</span>
                                  ) : null}
                                </div>
                                <p className="text-xs text-slate-500 mt-1">
                                  {formatListActivityTime(call.created_at)}
                                </p>
                                {call.contacted ? (
                                  <div className="flex flex-wrap items-center gap-2 mt-2">
                                    <Badge variant="secondary" className="text-xs font-normal text-slate-600">
                                      Contacted
                                    </Badge>
                                  </div>
                                ) : null}
                              </div>
                            </div>
                            <div className="flex items-center gap-2 shrink-0 self-start pt-0.5">
                              {callIntent ? (
                                <span
                                  className="inline-flex"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                  }}
                                >
                                  <IntentSemanticsIcon rawValue={callIntent} size={16} />
                                </span>
                              ) : null}
                              <ChevronRight className="w-4 h-4 text-slate-400" aria-hidden />
                            </div>
                          </div>
                        </div>
                      ) : (
                        <Link
                          to={`/calls/${call.call_id}`}
                          className={cn(
                            'flex-1 min-w-0 p-4 hover:bg-slate-50 transition-colors flex',
                            call.contacted && 'hover:bg-slate-100/80',
                          )}
                        >
                          <div className="flex items-start justify-between gap-4 w-full">
                            <>
                              <div className="flex items-start gap-4 flex-1 min-w-0">
                                <div
                                  className={cn(
                                    'w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5',
                                    call.contacted ? 'bg-slate-200' : 'bg-blue-50',
                                  )}
                                >
                                  <PhoneCall
                                    className={cn('w-5 h-5', call.contacted ? 'text-slate-500' : 'text-blue-600')}
                                  />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                                    <p
                                      className={cn(
                                        'font-medium',
                                        call.contacted ? 'text-slate-600' : 'text-slate-900',
                                      )}
                                    >
                                      Call #{call.call_id.slice(-8)}
                                    </p>
                                    {call.contacted && (
                                      <Badge variant="secondary" className="text-xs font-normal text-slate-600">
                                        Contacted
                                      </Badge>
                                    )}
                                    <Badge variant={call.status === 'completed' ? 'default' : 'secondary'} className="text-xs">
                                      {call.status}
                                    </Badge>
                                    {call.agent?.name && (
                                      <Badge variant="outline" className="text-xs">
                                        {call.agent.name}
                                      </Badge>
                                    )}
                                    {(callIntent || customerSentiment) && (
                                      <div
                                        className="flex items-center gap-1.5"
                                        onClick={(e) => {
                                          e.preventDefault();
                                          e.stopPropagation();
                                        }}
                                      >
                                        <IntentSemanticsIcon rawValue={callIntent} size={15} />
                                        <SentimentSemanticsIcon rawValue={customerSentiment} size={15} />
                                      </div>
                                    )}
                                  </div>
                                  <p className="text-sm text-slate-500 mb-2">
                                    {formatDateTime(call.created_at)}
                                  </p>
                                  {call.summary && (
                                    <div className="flex items-start gap-2 mt-2 p-2 bg-slate-50 rounded-lg">
                                      <MessageSquare className="w-4 h-4 text-slate-400 flex-shrink-0 mt-0.5" />
                                      <p className="text-sm text-slate-600 line-clamp-2">{call.summary}</p>
                                    </div>
                                  )}
                                </div>
                              </div>
                              <div className="flex flex-col items-end gap-2 flex-shrink-0">
                                <div className="flex items-center gap-4 text-sm text-slate-500">
                                  <div className="flex items-center gap-1">
                                    <Clock className="w-4 h-4" />
                                    {call.minutes_used} min
                                  </div>
                                </div>
                                <ChevronRight className="w-4 h-4 text-slate-400 mt-1" />
                              </div>
                            </>
                          </div>
                        </Link>
                      )}
                      <div
                        className="flex items-center border-l border-slate-100 shrink-0"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-full rounded-none px-3"
                              aria-label="Call actions"
                              onClick={(e) => e.preventDefault()}
                            >
                              <MoreVertical className="w-4 h-4 text-slate-500" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            {call.contacted ? (
                              <DropdownMenuItem
                                disabled={markingCallId === call.call_id}
                                onClick={(e) => handleSetCallContacted(call, false, e)}
                              >
                                <RotateCcw className="w-4 h-4 mr-2" />
                                Mark as not contacted
                              </DropdownMenuItem>
                            ) : (
                              <DropdownMenuItem
                                disabled={markingCallId === call.call_id}
                                onClick={(e) => handleSetCallContacted(call, true, e)}
                              >
                                <UserCheck className="w-4 h-4 mr-2" />
                                Mark as contacted
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between p-4 border-t border-slate-100">
                  <p className="text-sm text-slate-500">
                    Showing {page * limit + 1} to {Math.min((page + 1) * limit, total)} of {total} calls
                  </p>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => p - 1)}
                      disabled={page === 0}
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <span className="text-sm text-slate-600 px-2">
                      Page {page + 1} of {totalPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => p + 1)}
                      disabled={page >= totalPages - 1}
                    >
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export function CallDetailPage() {
  const { callId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [call, setCall] = useState(null);
  const [contactedUpdating, setContactedUpdating] = useState(false);

  useEffect(() => {
    loadCall();
  }, [callId]);

  const loadCall = async () => {
    try {
      const response = await getCallDetail(callId);
      setCall(response.data);
    } catch (error) {
      toast.error('Failed to load call details');
      navigate('/calls');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleCallContacted = async () => {
    if (!call?.call_id) return;
    const next = !call.contacted;
    setContactedUpdating(true);
    try {
      await patchCall(call.call_id, { contacted: next });
      toast.success(next ? 'Marked as contacted' : 'Marked as not contacted');
      await loadCall();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update call');
    } finally {
      setContactedUpdating(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <Card className="border-0 shadow-md">
          <CardContent className="p-6 space-y-4">
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-48 w-full" />
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!call) {
    return null;
  }

  const getCallValue = (...keys) => {
    for (const key of keys) {
      const value = call?.[key];
      if (value !== undefined && value !== null && value !== '') return value;
    }
    return null;
  };

  const customerDetails = call.customerDetails || call.customer_details || {};
  const linkedCustomer = call.customer || call.linked_customer || {};
  const linkedCustomerId = getCallValue(
    'linked_customer_id',
    'customer_id',
    'linkedCustomerId',
    'linked_customer',
    'customerId'
  );
  const callerName =
    customerDetails.callerName ||
    customerDetails.caller_name ||
    linkedCustomer.name ||
    linkedCustomer.full_name ||
    getCallValue('customerName', 'customer_name');
  const customerEmail =
    customerDetails.contactEmail ||
    customerDetails.contact_email ||
    linkedCustomer.email ||
    getCallValue('customerEmail', 'customer_email');
  const contactPhone =
    customerDetails.contactPhone ||
    customerDetails.contact_phone ||
    linkedCustomer.phone ||
    getCallValue('customerPhone', 'customer_phone', 'caller_number', 'from');
  const serviceNeeded =
    customerDetails.serviceNeeded ||
    customerDetails.service_needed ||
    getCallValue('serviceNeeded', 'service_needed');
  const propertyAddress =
    customerDetails.propertyAddress ||
    customerDetails.property_address ||
    getCallValue('customerAddress', 'customer_address', 'propertyAddress', 'property_address');
  const urgency =
    customerDetails.urgency ||
    getCallValue('urgency');
  const preferredDate =
    customerDetails.preferredDate ||
    customerDetails.preferred_date ||
    getCallValue('preferredDate', 'preferred_date');

  const intent = getCallIntent(call) || getCallValue('intent');
  const customerSentiment = getCallCustomerSentiment(call);
  const outcome = getCallValue('outcome');
  const followUpRequired = getCallValue('followUpRequired', 'follow_up_required');
  const actionsTaken = getCallValue('actionsTaken', 'actionTaken', 'actions_taken');

  const fromNumber = getCallValue('from');
  const toNumber = getCallValue('to');
  const direction = getCallValue('direction');
  const callSid = getCallValue('callSid', 'call_id');

  return (
    <div className="space-y-6 animate-fade-in" data-testid="call-detail-page">
      {/* Header */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/calls')}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-jakarta font-bold text-slate-900">
              Call #{call.call_id.slice(-8)}
            </h1>
            <p className="text-slate-500">{formatDateTime(call.created_at)}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap justify-end">
          <Button
            variant="outline"
            disabled={contactedUpdating}
            onClick={handleToggleCallContacted}
            className={call.contacted ? 'text-slate-600' : ''}
          >
            {call.contacted ? (
              <>
                <RotateCcw className="w-4 h-4 mr-2" />
                Mark as not contacted
              </>
            ) : (
              <>
                <UserCheck className="w-4 h-4 mr-2" />
                Mark as contacted
              </>
            )}
          </Button>
          {call.agent?.agent_id && (
            <Button variant="outline" onClick={() => navigate(`/agents/${call.agent.agent_id}`)}>
              <PhoneCall className="w-4 h-4 mr-2" />
              View Agent
            </Button>
          )}
          {linkedCustomerId && (
            <Button variant="outline" onClick={() => navigate(`/customers/${linkedCustomerId}`)}>
              <User className="w-4 h-4 mr-2" />
              View Customer
              <ExternalLink className="w-3 h-3 ml-1" />
            </Button>
          )}
        </div>
      </div>

      {/* Linked Customer Banner */}
      {linkedCustomerId && (
        <Card className="border-green-200 bg-green-50">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                <User className="w-5 h-5 text-green-600" />
              </div>
              <div className="flex-1">
                <p className="font-medium text-green-900">This call is linked to a customer</p>
                <p className="text-sm text-green-700">Customer and job records can be managed from the customer profile.</p>
              </div>
              <Button variant="outline" size="sm" onClick={() => navigate(`/customers/${linkedCustomerId}`)}>
                View Customer
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Customer Details (includes minutes used) */}
      {(linkedCustomerId ||
        callerName ||
        customerEmail ||
        contactPhone ||
        serviceNeeded ||
        propertyAddress ||
        urgency ||
        preferredDate ||
        call.minutes_used != null) && (
        <Card className="border-0 shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg">Customer Details</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {call.minutes_used != null && (
                <div>
                  <p className="text-xs text-slate-500">Minutes Used</p>
                  <p className="font-medium text-slate-900">{call.minutes_used} min</p>
                </div>
              )}
              {linkedCustomerId && (
                <div>
                  <p className="text-xs text-slate-500">Customer ID</p>
                  <p className="font-medium text-slate-900 break-all">{linkedCustomerId}</p>
                </div>
              )}
              {callerName && (
                <div>
                  <p className="text-xs text-slate-500">Caller Name</p>
                  <p className="font-medium text-slate-900">{callerName}</p>
                </div>
              )}
              {customerEmail && (
                <div>
                  <p className="text-xs text-slate-500">Email</p>
                  <p className="font-medium text-slate-900">{customerEmail}</p>
                </div>
              )}
              {contactPhone && (
                <div>
                  <p className="text-xs text-slate-500">Contact Phone</p>
                  <a
                    href={`tel:${String(contactPhone).replace(/\s+/g, '')}`}
                    className="font-medium text-teal-700 hover:text-teal-800 hover:underline"
                  >
                    {contactPhone}
                  </a>
                </div>
              )}
              {serviceNeeded && (
                <div>
                  <p className="text-xs text-slate-500">Service Needed</p>
                  <p className="font-medium text-slate-900">{serviceNeeded}</p>
                </div>
              )}
              {propertyAddress && (
                <div>
                  <p className="text-xs text-slate-500">Property Address</p>
                  <p className="font-medium text-slate-900">{propertyAddress}</p>
                </div>
              )}
              {urgency && (
                <div>
                  <p className="text-xs text-slate-500">Urgency</p>
                  <p className="font-medium text-slate-900">{urgency}</p>
                </div>
              )}
              {preferredDate && (
                <div>
                  <p className="text-xs text-slate-500">Preferred Date</p>
                  <p className="font-medium text-slate-900">{preferredDate}</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Summary */}
      {call.summary && (
        <Card className="border-0 shadow-md">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <FileText className="w-5 h-5 text-blue-600" />
              Summary
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-slate-700 leading-relaxed" data-testid="call-summary">
              {call.summary}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Call Summary Details */}
      {(intent || customerSentiment || outcome || Array.isArray(actionsTaken) || typeof followUpRequired === 'boolean') && (
        <Card className="border-0 shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg">Call Summary Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {(intent || customerSentiment) && (
              <div className="flex flex-wrap items-center gap-3">
                <IntentSemanticsIcon rawValue={intent} size={18} />
                <SentimentSemanticsIcon rawValue={customerSentiment} size={18} />
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {outcome && (
                <div>
                  <p className="text-xs text-slate-500">Outcome</p>
                  <p className="font-medium text-slate-900 break-words">{outcome}</p>
                </div>
              )}
              {typeof followUpRequired === 'boolean' && (
                <div>
                  <p className="text-xs text-slate-500">Follow-up Required</p>
                  <p className="font-medium text-slate-900">{followUpRequired ? 'Yes' : 'No'}</p>
                </div>
              )}
            </div>

            {Array.isArray(actionsTaken) && actionsTaken.length > 0 && (
              <div>
                <p className="text-xs text-slate-500 mb-2">Actions Taken</p>
                <ul className="space-y-2">
                  {actionsTaken.map((action, index) => (
                    <li key={`${index}-${action}`} className="text-sm text-slate-700">
                      {index + 1}. {action}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Recording */}
      {call.recording_url && (
        <Card className="border-0 shadow-md">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Play className="w-5 h-5 text-blue-600" />
              Recording
            </CardTitle>
          </CardHeader>
          <CardContent>
            <audio controls className="w-full" data-testid="call-recording">
              <source src={call.recording_url} type="audio/mpeg" />
              Your browser does not support the audio element.
            </audio>
          </CardContent>
        </Card>
      )}

      {/* Transcript */}
      {call.transcript && (
        <Card className="border-0 shadow-md">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <FileText className="w-5 h-5 text-blue-600" />
              Transcript
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[400px] rounded-lg border border-slate-200 p-4">
              <pre className="whitespace-pre-wrap text-sm text-slate-700 font-mono" data-testid="call-transcript">
                {call.transcript}
              </pre>
            </ScrollArea>
          </CardContent>
        </Card>
      )}

      {/* No transcript/summary message */}
      {!call.transcript && !call.summary && (
        <Card className="border-0 shadow-md">
          <CardContent className="p-8 text-center">
            <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">No transcript or summary available for this call.</p>
          </CardContent>
        </Card>
      )}

      {call.agent && (
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500">Agent</p>
                <p className="font-semibold text-slate-900">{call.agent.name || call.agent.agent_id}</p>
                {call.agent.agent_id && <p className="text-xs text-slate-500">Agent ID: {call.agent.agent_id}</p>}
              </div>
              {call.agent.agent_id && (
                <Button variant="outline" size="sm" onClick={() => navigate(`/agents/${call.agent.agent_id}`)}>
                  Open Agent
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {(direction || fromNumber || toNumber || callSid) && (
        <Card className="border-0 shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg">Call Metadata</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {direction && (
                <div>
                  <p className="text-xs text-slate-500">Direction</p>
                  <p className="font-medium text-slate-900 capitalize">{direction}</p>
                </div>
              )}
              {fromNumber && (
                <div>
                  <p className="text-xs text-slate-500">From</p>
                  <p className="font-medium text-slate-900">{fromNumber}</p>
                </div>
              )}
              {toNumber && (
                <div>
                  <p className="text-xs text-slate-500">To</p>
                  <p className="font-medium text-slate-900">{toNumber}</p>
                </div>
              )}
              {callSid && (
                <div>
                  <p className="text-xs text-slate-500">Call SID</p>
                  <p className="font-medium text-slate-900 break-all">{callSid}</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
